import {
  ProCard,
  ProForm,
  ProFormDatePicker,
  ProFormDigit,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
} from "@ant-design/pro-components";
import { Form, List, message, Modal, Tag } from "antd";
import { useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useLocale, useT } from "@trust/i18n";
import {
  checkDuplicateGuides,
  createGuide,
  type GuideCreateIn,
  type SimilarGuideItem,
} from "@/features/guides/api";
import { listGuideFields, type GuideFieldOut } from "@/features/admin/api";

const LANGUAGE_OPTS = [
  { value: "zh", label: "中文" },
  { value: "en", label: "英文" },
  { value: "mixed", label: "中英文" },
];
const EVIDENCE_GRADE_OPTS = [
  { value: "1A", label: "1A — 系统评价 / Meta" },
  { value: "1B", label: "1B — 高质量 RCT" },
  { value: "2A", label: "2A — 队列研究" },
  { value: "2B", label: "2B — 病例对照" },
  { value: "3", label: "3 — 专家共识" },
];
const CONSENSUS_OPTS = [
  { value: "delphi", label: "Delphi 法" },
  { value: "nominal_group", label: "名义小组法" },
  { value: "informal", label: "非正式讨论" },
  { value: "none", label: "未明确" },
];
const COI_OPTS = [
  { value: "yes", label: "是 - 全部声明" },
  { value: "partial", label: "部分声明" },
  { value: "no", label: "未声明" },
];

/** P1.4 动态扩展字段:按 field_type 渲染对应的 ProForm 控件,值写入 extra_fields[field] */
function ExtraFieldItem({ field, locale }: { field: GuideFieldOut; locale: string }) {
  const t = useT();
  const label = locale === "en" ? field.label_en || field.label : field.label;
  const common = {
    name: ["extra_fields", field.field],
    label,
    initialValue: field.default_val ?? undefined,
    rules: field.required ? [{ required: true, message: t("guideFields.requiredMsg") }] : [],
  };
  switch (field.field_type) {
    case "textarea":
      return <ProFormTextArea {...common} fieldProps={{ rows: 3 }} />;
    case "date":
      return <ProFormDatePicker {...common} />;
    case "number":
      return <ProFormDigit {...common} initialValue={undefined} />;
    case "select":
      return (
        <ProFormSelect
          {...common}
          options={(field.options ?? []).map((o) => ({
            value: o.value,
            label: (locale === "en" ? o.label_en || o.label : o.label) || o.value,
          }))}
        />
      );
    default:
      return <ProFormText {...common} />;
  }
}

export function NewGuidePage() {
  const navigate = useNavigate();
  const [msgApi, msgCtx] = message.useMessage();
  const t = useT();
  const locale = useLocale();
  const [form] = Form.useForm<GuideCreateIn>();

  // 查重(需求 #19 / 2.6):标题失焦与提交前各查一次,命中则弹窗提示,可"仍要创建"
  const [dupItems, setDupItems] = useState<SimilarGuideItem[]>([]);
  const [dupOpen, setDupOpen] = useState(false);
  const dupAckRef = useRef(false); // 用户已在弹窗确认过"仍要创建"(标题再改则作废)

  /** 调 check-duplicate;提示性质 — 无命中/失败都返回空,不阻塞流程 */
  const runDupCheck = async (values: GuideCreateIn): Promise<SimilarGuideItem[]> => {
    const title = values.title?.trim();
    if (!title || title.length < 2) return [];
    try {
      const resp = await checkDuplicateGuides({
        title,
        title_en: values.title_en || undefined,
        subject_classification: values.subject_classification || undefined,
      });
      return resp.items;
    } catch {
      return [];
    }
  };

  // 动态扩展字段配置(注册阶段):公开接口,按 order_num 排序后按 group_name 分组渲染
  const fieldsQ = useQuery({
    queryKey: ["guide-fields", "Register"],
    queryFn: () => listGuideFields({ stage: "Register" }),
  });
  const fieldGroups = (() => {
    const groups = new Map<string, GuideFieldOut[]>();
    for (const f of fieldsQ.data ?? []) {
      const g = f.group_name || "";
      const arr = groups.get(g);
      if (arr) arr.push(f);
      else groups.set(g, [f]);
    }
    return [...groups.entries()];
  })();

  return (
    <div style={{ padding: "16px 32px" }}>
      {msgCtx}
      <ProCard title="提交新指南" bordered>
        <ProForm<GuideCreateIn>
          form={form}
          layout="horizontal"
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 15 }}
          onFinish={async (values) => {
            try {
              // 提交前查重:有命中且未确认过 → 弹窗阻断,等用户点"仍要创建"
              if (!dupAckRef.current) {
                const items = await runDupCheck(values);
                if (items.length) {
                  setDupItems(items);
                  setDupOpen(true);
                  return false;
                }
              }
              const g = await createGuide({
                ...values,
                release_date: values.release_date || undefined,
              });
              msgApi.success(`已创建,注册号 ${g.reg_no}`);
              navigate({ to: "/inx/guide/$guideId", params: { guideId: String(g.id) } });
              return true;
            } catch (exc) {
              msgApi.error((exc as Error).message);
              return false;
            }
          }}
          submitter={{
            searchConfig: { submitText: "创建指南", resetText: "清空" },
          }}
        >
          <ProCard title="基础标识" type="inner" style={{ marginBottom: 12 }}>
            <ProFormText
              name="title"
              label="中文标题"
              rules={[{ required: true, min: 2, max: 512 }]}
              placeholder="例:WHO 2 型糖尿病管理指南 2026"
              fieldProps={{
                onChange: () => {
                  dupAckRef.current = false; // 标题改了,之前的"仍要创建"作废
                },
                onBlur: async () => {
                  if (dupAckRef.current) return;
                  const items = await runDupCheck(form.getFieldsValue());
                  if (items.length) {
                    setDupItems(items);
                    setDupOpen(true);
                  }
                },
              }}
            />
            <ProFormText name="title_en" label="英文标题" />
            <ProFormSelect
              name="type"
              label="类型"
              options={[
                { value: "指南", label: "指南" },
                { value: "标准", label: "标准" },
                { value: "专家共识", label: "专家共识" },
              ]}
            />
            <ProFormText name="edition" label="版次" />
            <ProFormText name="register" label="注册单位" />
            <ProFormText name="release_org" label="发布机构" />
            <ProFormDatePicker name="release_date" label="发布日期" />
            <ProFormText
              name="country"
              label="国别"
              placeholder="International / China / USA ..."
            />
            <ProFormText name="subject_classification" label="学科分类" />
            <ProFormText name="population_classification" label="人群分类" />
            <ProFormText name="journal" label="期刊" />
            <ProFormTextArea name="origin_link" label="原文链接" fieldProps={{ rows: 2 }} />
            <ProFormTextArea name="pdf_url" label="PDF URL" fieldProps={{ rows: 2 }} />
          </ProCard>

          <ProCard title="基本信息" type="inner" style={{ marginBottom: 12 }}>
            <ProFormText
              name="keywords"
              label="关键词"
              placeholder="分号分隔,如:糖尿病;二甲双胍;RCT"
              tooltip="用于搜索与分类"
            />
            <ProFormTextArea name="abstract" label="摘要" fieldProps={{ rows: 3 }} />
            <ProFormSelect name="language" label="语言" options={LANGUAGE_OPTS} />
          </ProCard>

          <ProCard title="临床" type="inner" style={{ marginBottom: 12 }}>
            <ProFormTextArea
              name="clinical_question"
              label="临床问题"
              tooltip="PICO 描述:人群 / 干预 / 对照 / 结局"
              fieldProps={{ rows: 3 }}
            />
            <ProFormText name="target_population" label="目标人群" />
            <ProFormDigit name="recommendation_count" label="推荐意见数量" min={0} />
          </ProCard>

          <ProCard title="方法学" type="inner" style={{ marginBottom: 12 }}>
            <ProFormSelect
              name="evidence_grade"
              label="最高证据等级"
              options={EVIDENCE_GRADE_OPTS}
            />
            <ProFormSelect name="consensus_method" label="共识方法" options={CONSENSUS_OPTS} />
          </ProCard>

          <ProCard title="资助与利益冲突" type="inner" style={{ marginBottom: 12 }}>
            <ProFormDigit
              name="funding_amount_cny"
              label="资助金额(元)"
              min={0}
              fieldProps={{ precision: 2 }}
            />
            <ProFormSelect name="coi_declared" label="利益冲突声明" options={COI_OPTS} />
          </ProCard>

          {/* P1.4 动态扩展字段(guide_fields 配置驱动,值进 extra_fields)*/}
          {fieldGroups.map(([groupName, fields]) => (
            <ProCard
              key={groupName || "_"}
              title={groupName || t("guideFields.sectionTitle")}
              type="inner"
              style={{ marginBottom: 12 }}
            >
              {fields.map((f) => (
                <ExtraFieldItem key={f.id} field={f} locale={locale} />
              ))}
            </ProCard>
          ))}
        </ProForm>

        {/* P2.6 查重命中提示(需求 #19):列表可跳转详情,确认后"仍要创建"继续提交 */}
        <Modal
          title={t("duplicateCheck.title")}
          open={dupOpen}
          okText={t("duplicateCheck.createAnyway")}
          cancelText={t("duplicateCheck.cancel")}
          onOk={() => {
            dupAckRef.current = true;
            setDupOpen(false);
            form.submit();
          }}
          onCancel={() => setDupOpen(false)}
        >
          <p>{t("duplicateCheck.desc")}</p>
          <List
            size="small"
            dataSource={dupItems}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Link
                    key="view"
                    to="/inx/guide/$guideId"
                    params={{ guideId: String(item.guide_id) }}
                    target="_blank"
                  >
                    {t("duplicateCheck.view")}
                  </Link>,
                ]}
              >
                <List.Item.Meta
                  title={item.title}
                  description={
                    item.reg_no +
                    (item.subject_classification ? ` · ${item.subject_classification}` : "")
                  }
                />
                <Tag color="orange">
                  {t("duplicateCheck.similarity")} {Math.round(item.score * 100)}%
                </Tag>
              </List.Item>
            )}
          />
        </Modal>
      </ProCard>
    </div>
  );
}
